import React from 'react'
import styled from 'styled-components'
import { H4, P } from '@jarvis.oddle/core/components/Typo'
import Flex from '@jarvis.oddle/core/components/Flex'
import { InlineNotification, notificationStates } from '@jarvis.oddle/core/components/InlineNotification'
import { iconSizes, margin, marginSizes } from '@jarvis.oddle/core/theme/default'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import IconPickupStore from '@jarvis.oddle/core/components/Icons/IconPickupStore'
import IconAddressPin from '@jarvis.oddle/core/components/Icons/IconAddressPin'
import IconCheckCircle from '@jarvis.oddle/core/components/Icons/IconCheckCircle'
import IconUncheckCircle from '@jarvis.oddle/core/components/Icons/IconUncheckCircle'
import Collapsible from '@jarvis.oddle/core/components/Collapsible'
import { isEmpty, formatDisplacement } from 'src/utils/string'
import { useAppContext } from 'src/containers/AppContext'

const StyledNotification = styled(InlineNotification)`
  width: 100%;
  margin-top: ${margin[marginSizes.small]};
`

// Expects a string with format 'dd mm yyyy'
// This is specific to store.earliestCollectionDate
const parseEarliestCollectionDate = (date) => {
  const params = date.split(' ')
  return new Date(params[2], params[1] - 1, params[0])
}

const StoreInformationCard = ({
  store,
  selected,
  showInfo,
  showCollectionTime,
  shouldTruncateInfo
}: {
  store: {
    name: string
    address: string
    earliestCollectionDate: string
    earliestCollectionTime: string
    earliestCollectionEndTime: string
    displacement: number
  }
  selected: boolean
  showInfo: boolean
  showCollectionTime: boolean
  shouldTruncateInfo?: boolean
}): JSX.Element => {
  const { t } = React.useContext(TranslationContext)
  const { formatFullDate } = useAppContext()
  const {
    name,
    address,
    earliestCollectionDate,
    earliestCollectionTime,
    earliestCollectionEndTime,
    displacement
  } = store || {}

  const isTimeInformationAvailable = !isEmpty(earliestCollectionDate) && !isEmpty(earliestCollectionTime) && !isEmpty(earliestCollectionEndTime)

  return (
    <Flex width='100%' flexDirection='column' justifyContent='space-between'>
      <Flex
        width='100%'
        flexDirection='row'
        justifyContent='flex-start'
        alignItems='center'
        marginTop={marginSizes.extraSmall}
        marginBottom={marginSizes.extraSmall}>
        <Flex marginRight={marginSizes.small} opacity={selected ? 1 : 0.5}>
          {selected
            ? <IconCheckCircle iconSize={iconSizes.medium} />
            : <IconUncheckCircle iconSize={iconSizes.medium} />
          }
        </Flex>
        <H4 bold normalised line={shouldTruncateInfo ? 1 : undefined}>{name}</H4>
      </Flex>
      <Collapsible show={showInfo}>
        {!isNaN(displacement) && (
          <Flex
            width='100%'
            flexDirection='row'
            justifyContent='flex-start'
            alignItems='center'
            marginTop={marginSizes.extraSmall}
            marginBottom={marginSizes.extraSmall}
            opacity={selected ? 1 : 0.5}>
            <Flex marginRight={marginSizes.small}>
              <IconAddressPin iconSize={iconSizes.medium} />
            </Flex>
            <P
              margin={marginSizes.none}
              line={shouldTruncateInfo ? 1 : undefined}>
              {t('_displacement_ km away', { displacement: formatDisplacement(displacement, '###,##0.00') })}
            </P>
          </Flex>
        )}
        <Flex
          width='100%'
          flexDirection='row'
          justifyContent='flex-start'
          alignItems='flex-start'
          marginTop={marginSizes.extraSmall}
          marginBottom={marginSizes.extraSmall}
          opacity={selected ? 1 : 0.5}>
          <Flex marginRight={marginSizes.small}>
            <IconPickupStore iconSize={iconSizes.medium} />
          </Flex>
          <P
            margin={marginSizes.none}
            line={shouldTruncateInfo ? 2 : undefined}>
            {address}
          </P>
        </Flex>
        {showCollectionTime && isTimeInformationAvailable ? (
          <StyledNotification
            lightTheme
            withIcon={false}
            state={notificationStates.informative}
            text={t('Earliest Collection Time')}
            subText={`${formatFullDate(parseEarliestCollectionDate(earliestCollectionDate))} (${earliestCollectionTime} - ${earliestCollectionEndTime} )`} />
        ) : null}
      </Collapsible>
    </Flex>

  )
}

export default StoreInformationCard
